import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import ForcePasswordChange from "@/components/admin/ForcePasswordChange";
import {
  LayoutDashboard,
  FileText,
  LogOut,
  Menu,
  X,
  Home,
  Tag,
  BarChart3,
  Brain,
  Mail,
  AlertCircle,
  MessageSquare,
  Download,
  Folder,
  Smartphone,
  Users,
  Shield,
  Activity,
  Sparkles,
} from "lucide-react";

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout = ({ children }: AdminLayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Initialize session timeout monitoring
  useSessionTimeout();

  // Log admin login activity once per session/mount
  useEffect(() => {
    (async () => {
      try {
        const flagKey = "admin_login_logged_v1";
        if (sessionStorage.getItem(flagKey)) return;
        const { data: sess } = await supabase.auth.getSession();
        const uid = sess.session?.user.id;
        if (!uid) return;
        const ua = navigator.userAgent;
        const device = /Mobi|Android/i.test(ua) ? "Mobile" : /Tablet|iPad/i.test(ua) ? "Tablet" : "Desktop";
        const provider = (sess.session?.user.app_metadata as any)?.provider ?? "email";
        const loginMethod = provider === "google" ? "google" : provider === "apple" ? "apple" : "email";
        await supabase.from("admin_login_activity" as any).insert({
          user_id: uid,
          user_agent: ua,
          device,
          login_method: loginMethod,
        });
        await supabase.from("profiles").update({
          last_login_at: new Date().toISOString(),
          login_method: loginMethod,
        }).eq("id", uid);
        sessionStorage.setItem(flagKey, "1");
      } catch {
        // non-blocking
      }
    })();
  }, []);

  const menuItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
    { icon: FileText, label: "Blog Posts", path: "/admin/posts" },
    { icon: Brain, label: "Bulk Quiz Generator", path: "/admin/bulk-quiz-generator" },
    { icon: Sparkles, label: "Bulk Premium Guides", path: "/admin/bulk-premium-guide-generator" },
    { icon: Tag, label: "Categories", path: "/admin/categories" },
    { icon: Download, label: "Digital Products", path: "/admin/digital-products" },
    { icon: Folder, label: "Product Categories", path: "/admin/product-categories" },
    { icon: Mail, label: "Newsletter", path: "/admin/newsletter" },
    { icon: BarChart3, label: "Analytics", path: "/admin/analytics" },
    { icon: Smartphone, label: "Install Prompt", path: "/admin/install-analytics" },
    { icon: AlertCircle, label: "Issue Reports", path: "/admin/issue-reports" },
    { icon: MessageSquare, label: "Blog Requests", path: "/admin/blog-requests" },
    { icon: Users, label: "Team Members", path: "/admin/team" },
    { icon: Shield, label: "Roles & Permissions", path: "/admin/team/roles" },
    { icon: Activity, label: "Login Activity", path: "/admin/team/activity" },
  ];

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
      });
      navigate("/admin/login");
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to log out. Please try again.",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-50 flex items-center justify-between px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
        <h1 className="text-lg font-bold">Admin Panel</h1>
        <div className="w-10" />
      </div>

      {/* Desktop Header with Menu Toggle */}
      <div className="hidden lg:flex fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-50 items-center px-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <Menu className="h-5 w-5" />
        </Button>
        <h1 className="ml-4 text-xl font-bold">Admin Panel</h1>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-screen lg:h-[calc(100vh-4rem)] bg-card border-r border-border transition-transform duration-300 z-40 w-64 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:mt-16`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-border">
            <h1 className="text-xl font-bold">Admin Panel</h1>
            <Button
              variant="ghost"
              size="icon"
              asChild
              className="lg:hidden"
            >
              <Link to="/">
                <Home className="h-5 w-5" />
              </Link>
            </Button>
          </div>

          {/* Menu Items */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted text-muted-foreground"
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Back to Blog & Logout */}
          <div className="p-4 border-t border-border space-y-2">
            <Button
              variant="outline"
              className="w-full justify-start gap-3"
              asChild
            >
              <Link to="/">
                <Home className="h-5 w-5" />
                <span>Back to Blog</span>
              </Link>
            </Button>
            
            <Button
              variant="outline"
              className="w-full justify-start gap-3"
              onClick={handleLogout}
            >
              <LogOut className="h-5 w-5" />
              <span>Logout</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`pt-16 transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-0'}`}>
        <div className="p-6 lg:p-8">{children}</div>
      </main>

      <ForcePasswordChange />

      {/* Overlay for mobile only */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-background/80 backdrop-blur-sm z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
